import React, { useState, useRef, useEffect, useCallback,} from "react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from '../contexts/AuthContext';
import type { AlbumImage } from "@/types/AlbumSaveData";
import { jwtDecode } from 'jwt-decode';
import { Tooltip, Menu, MenuItem, } from '@mui/material';
import LockIcon from "@mui/icons-material/Lock";
import exifr from "exifr";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// resize worker (single)
const resizeWorker = new Worker(
    new URL("../workers/resize.worker.ts", import.meta.url)
);

const uploadSmallWorker = new Worker(
    new URL("../workers/uploadSmall.worker.ts", import.meta.url)
);


interface PhotoUploaderProps {
    albumName: string;
    disableWallArts?: boolean;
    onUploadComplete?: (images: AlbumImage[]) => void;
    onImagesUploaded?: (images: AlbumImage[]) => void; //
    onImagesSaved?: (
        metadata: { name: string; orientation: "horizontal" | "vertical" }[]
    ) => Promise<void>;
    defaultImages?: AlbumImage[];
    usedImages: AlbumImage[]; // <-- includes used: true for images in use
    onExportPrint: () => void;
    onExportProof: () => void;
    onExportWebProof: () => void;
    printExported?: boolean;   // <-- new
    width: number;
    setWidth: React.Dispatch<React.SetStateAction<number>>;
    columns?: number; //  LIVE value from parent (NOT initial)
    onThumbnailColumnsChange?: (columns: number) => void;
    onRequestSlideshowNames?: () => void; // <-- add this
}


interface TokenPayload {
    username: string;
    id?: string;
    exp?: number;
    email: string;
    fullName: string;
}


const PhotoUploader: React.FC<PhotoUploaderProps> = ({
    albumName,
    disableWallArts,
    defaultImages = [],
    onImagesUploaded,
    usedImages,
    onExportPrint,
    onExportProof,
    onExportWebProof,
    onImagesSaved,
    width,
    setWidth,
    printExported,
    onThumbnailColumnsChange,
    onRequestSlideshowNames,
    columns = 2, //  IMPORTANT default fallback

}) => {


    const fileInputRef = useRef<HTMLInputElement>(null);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [zoomedImage, setZoomedImage] = useState<AlbumImage | null>(null);
    const [zoomedImageOrientation, setZoomedImageOrientation] = useState<"horizontal" | "vertical" | null>(null);
    const { token, } = useAuth();
    const [username, setUsername] = useState("");
    const [userFullName, setUserFullName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    //new for multi images
    const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);
    const [uploadMessage, setUploadMessage] = useState<string | null>(null);
    const [fade, setFade] = useState(false);
    //select with shift
    const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
    const API_URL = process.env.REACT_APP_API_URL;


    const originalWorkers = useRef<Worker[]>([]);
    const workerBusy = useRef<boolean[]>([]);
    const uploadQueue = useRef<any[]>([]);
    const ORIGINAL_WORKERS_COUNT = 3;
    const [batchCompleted, setBatchCompleted] = useState(0);
    const [isImporting, setIsImporting] = useState(false);
    const [showImportOverlay, setShowImportOverlay] = useState(false);
    const [importProgress, setImportProgress] = useState(0); // 0 to 1
    const [importMessage, setImportMessage] = useState("Uploading Images...");
    const [animationPosition, setAnimationPosition] = useState(0); // 0% to 100%
    const completedRef = useRef(0);
    type SortMode = "name" | "dateTaken" | "size";
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    const [sortMode, setSortMode] = useState<SortMode>("name");


    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const printExportedRef = useRef(false);


    const handlePrint = useCallback(() => {
        if (printExportedRef.current) return;

        printExportedRef.current = true;

        handleClose();

        Promise.resolve(onExportPrint()).finally(() => {
            // keep locked if export should only run once
            // OR unlock if you want retry behavior
        });
    }, [handleClose, onExportPrint]);


    useEffect(() => {
        if (!usedImages) return;

        setImages(prev =>
            prev.map(img => {
                const usedImg = usedImages.find(u => u.id === img.id);
                return usedImg ? { ...img, used: true } : img; // <-- do not force false
            })
        );
    }, [usedImages]);



    //get the login info and ther user info
    useEffect(() => {
        if (token) {
            try {
                const decoded = jwtDecode<TokenPayload>(token);
                setUserFullName(decoded.fullName || "");
                setUserEmail(decoded.email || "");
                setUsername(decoded.username);
            } catch (err) {
                console.error("Invalid token", err);
                setUserFullName("");
                setUserEmail("");
            }
        }
    }, [token]);


    //for the image zoomed
    useEffect(() => {
        if (!zoomedImage) return;

        const img = new Image();
        img.src = zoomedImage.smallUrl;
        img.onload = () => {
            if (img.naturalWidth >= img.naturalHeight) {
                setZoomedImageOrientation("horizontal");
            } else {
                setZoomedImageOrientation("vertical");
            }
        };
    }, [zoomedImage]);


    const directionRef = useRef<"left" | "right">("right");
    useEffect(() => {
        if (!isImporting) return;

        const interval = setInterval(() => {
            setAnimationPosition(prev => {
                let next = prev;

                if (directionRef.current === "right") {
                    next = prev + 2;
                    if (next >= 100) {
                        next = 100;
                        directionRef.current = "left";
                    }
                } else {
                    next = prev - 2;
                    if (next <= 0) {
                        next = 0;
                        directionRef.current = "right";
                    }
                }

                return next;
            });
        }, 16);

        return () => clearInterval(interval);
    }, [isImporting]);


    //Mark the images after the first import
    useEffect(() => {
        if (!isImporting && images.length > 0) {
            onImagesUploaded?.(images.map(i => ({ ...i })));
        }
    }, [isImporting]);

    // show message when images exist
    const showUploadMessage = (message: string, duration = 4000) => {
        setUploadMessage(message);
        setFade(false);

        // start fade 1 second before disappearing
        setTimeout(() => setFade(true), duration - 1000);

        // clear message after full duration
        setTimeout(() => {
            setUploadMessage('');
            setFade(false);
        }, duration);
    };


    // Handle file uploads and check for duplicates
    // Resize worker queue

    // -------------- Resize queue --------------
    const enqueueOriginalUpload = (job: any): Promise<string> => {
        return new Promise((resolve, reject) => {
            pendingOriginalUploads.current.set(job.id, {
                resolve,
                reject,
            });

            uploadQueue.current.push(job);
            dispatchOriginalUploads();
        });
    };


    const dispatchOriginalUploads = () => {
        for (let i = 0; i < originalWorkers.current.length; i++) {
            if (!workerBusy.current[i] && uploadQueue.current.length > 0) {
                const job = uploadQueue.current.shift();
                workerBusy.current[i] = true;
                originalWorkers.current[i].postMessage(job);
            }
        }
    };

    type PendingOriginalUpload = {
        resolve: (url: string) => void;
        reject: (error: Error) => void;
    };

    const pendingOriginalUploads = useRef(
        new Map<string, PendingOriginalUpload>()
    );

    // Worker wrapper
    const handleOriginalWorkerMessage = (
        e: MessageEvent,
        workerIndex: number
    ) => {
        const { id, originalUrl, progress, error } = e.data;

        // Update the image's progress and original URL.
        setImages(prev =>
            prev.map(img =>
                img.id === id
                    ? {
                        ...img,
                        originalUrl: originalUrl ?? img.originalUrl,
                        progress: progress ?? img.progress ?? 0,
                    }
                    : img
            )
        );

        // Upload progress is not the final server response.
        // Keep the worker busy until success or error arrives.
        if (!error && !originalUrl) {
            return;
        }

        workerBusy.current[workerIndex] = false;

        const pending = pendingOriginalUploads.current.get(id);
        pendingOriginalUploads.current.delete(id);

        if (error) {
            pending?.reject(new Error(error));
        } else if (originalUrl) {
            pending?.resolve(originalUrl);
        } else {
            pending?.reject(
                new Error("Original upload completed without an image URL")
            );
        }

        dispatchOriginalUploads();
    };

    useEffect(() => {
        originalWorkers.current = Array.from(
            { length: ORIGINAL_WORKERS_COUNT },
            () => new Worker(
                new URL("../workers/uploadOriginal.worker.ts", import.meta.url)
            )
        );

        workerBusy.current = new Array(ORIGINAL_WORKERS_COUNT).fill(false);

        originalWorkers.current.forEach((worker, index) => {
            worker.onmessage = (e) => {
                handleOriginalWorkerMessage(e, index);
            };
        });

        return () => {
            originalWorkers.current.forEach(w => w.terminate());
        };
    }, []);



    // resize
    const pendingResizes = new Map<
        string,
        {
            resolve: (r: { blob: Blob; width: number; height: number }) => void;
            reject: (e: Error) => void;
        }
    >();

    resizeWorker.onmessage = (e: MessageEvent) => {
        const { id, blob, width, height, error } = e.data;
        const pending = pendingResizes.get(id);
        if (!pending) return;

        pendingResizes.delete(id);

        if (error) pending.reject(new Error(error));
        else pending.resolve({ blob, width, height });
    };

    const resizeWithWorker = (file: File, maxDimension: number) =>
        new Promise<{ blob: Blob; width: number; height: number }>((resolve, reject) => {
            const id = uuidv4();

            const listener = (e: MessageEvent) => {
                if (e.data?.id !== id) return;
                resizeWorker.removeEventListener("message", listener);
                if (e.data.error) reject(e.data.error);
                else resolve(e.data);
            };

            resizeWorker.addEventListener("message", listener);
            resizeWorker.postMessage({ file, maxDimension, id });
        });

    // upload small
    // Map to track pending uploads
    const pendingUploads = new Map<
        string,
        {
            resolve: (result: { smallUrl: string }) => void;
            reject: (err: Error) => void;
        }
    >();

    const uploadSmallWithWorker = (
        blob: Blob,
        albumName: string,
        id: string,
        fileName: string
    ): Promise<{ smallUrl: string }> => {
        return new Promise((resolve, reject) => {
            // Store resolve/reject in the map
            pendingUploads.set(id, { resolve, reject });

            // Send data to the worker
            uploadSmallWorker.postMessage({
                blob,
                albumName,
                id,
                token,
                apiUrl: process.env.REACT_APP_API_URL,
                fileName,
            });
        });
    };

    // Single message listener for the worker
    uploadSmallWorker.addEventListener("message", (e: MessageEvent) => {
        const data = e.data;
        if (!data?.id) return;

        const pending = pendingUploads.get(data.id);
        if (!pending) return;

        pendingUploads.delete(data.id);

        if (data.error) pending.reject(new Error(data.error));
        else pending.resolve({ smallUrl: data.smallUrl });
    });

    uploadSmallWorker.onerror = (e) => {
        console.error("uploadSmallWorker crashed", e);
        // Reject all pending promises if worker crashes
        pendingUploads.forEach(({ reject }) =>
            reject(new Error("Small worker crashed"))
        );
        pendingUploads.clear();
    };

    const getOriginalSize = (file: File) =>
        new Promise<{ width: number; height: number }>((resolve) => {
            const img = new Image();
            const url = URL.createObjectURL(file);

            img.onload = () => {
                resolve({ width: img.width, height: img.height });
                URL.revokeObjectURL(url);
            };

            img.src = url;
        });


    class UploadManager {
        private queue: (() => Promise<void>)[] = [];
        private active = 0;
        maxConcurrent = 5;

        add(task: () => Promise<void>) {
            this.queue.push(task);
            this.next();
        }

        private next() {
            while (this.active < this.maxConcurrent && this.queue.length) {
                const task = this.queue.shift()!;
                this.active++;

                task()
                    .catch((err) => {
                        console.error("UPLOAD TASK FAILED", err);
                    })
                    .finally(() => {
                        this.active--;
                        this.next();
                    });
            }
        }

        waitForIdle(): Promise<void> {
            return new Promise(resolve => {
                const check = () => {
                    if (this.active === 0 && this.queue.length === 0) {
                        resolve();
                    } else {
                        setTimeout(check, 100);
                    }
                };
                check();
            });
        }
    }


    const handleFiles = async (files: FileList) => {
        if (isImporting) return;

        setIsImporting(true);

        const uploadManager = new UploadManager();
        uploadManager.maxConcurrent = 5;

        const fileArray = Array.from(files)
            .slice(0, 200)
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true })
            );

        const duplicates: string[] = [];
        const existingNames = new Set(images.map(img => img.name));
        const seenNames = new Set<string>();

        const newFiles: File[] = [];
        const repairFiles: File[] = [];

        // Separate new images from existing images that need their
        // originals uploaded again.
        for (const file of fileArray) {
            if (seenNames.has(file.name)) {
                duplicates.push(file.name);
                continue;
            }

            seenNames.add(file.name);

            if (existingNames.has(file.name)) {
                repairFiles.push(file);
            } else {
                newFiles.push(file);
            }
        }

        const totalTasks = newFiles.length + repairFiles.length;
        const uploadedImages: AlbumImage[] = [];
        const failedUploads: string[] = [];
        const repairedOriginals: string[] = [];

        completedRef.current = 0;

        setBatchCompleted(0);
        setAnimationPosition(0);
        directionRef.current = "right";

        setShowImportOverlay(true);
        setImportProgress(0);
        setImportMessage(`Uploading Images 0 / ${totalTasks}`);

        // Update progress only when an entire image task has finished.
        const updateTaskProgress = () => {
            completedRef.current++;

            const safeNext = Math.min(
                completedRef.current,
                totalTasks
            );

            setBatchCompleted(safeNext);

            setImportProgress(
                totalTasks > 0 ? safeNext / totalTasks : 1
            );

            setImportMessage(
                failedUploads.length > 0
                    ? `Uploading Images ${safeNext} / ${totalTasks} (${failedUploads.length} failed so far)`
                    : `Uploading Images ${safeNext} / ${totalTasks}`
            );
        };

        // NEW IMAGE: create and upload both small and original files.
        const processFile = async (file: File) => {
            let newImage: AlbumImage | null = null;

            try {
                const [exif, [{ blob: smallBlob, width, height }, originalSize]] =
                    await Promise.all([
                        exifr.parse(file).catch(() => null),
                        Promise.all([
                            resizeWithWorker(file, 1000),
                            getOriginalSize(file),
                        ]),
                    ]);

                const orientation: "horizontal" | "vertical" =
                    width >= height ? "horizontal" : "vertical";

                const takenAt = exif?.DateTimeOriginal ?? null;
                const tempSmallUrl = URL.createObjectURL(smallBlob);
                const id = uuidv4();

                newImage = {
                    id,
                    name: file.name,
                    smallUrl: tempSmallUrl,
                    originalUrl: "",
                    width,
                    height,
                    originalWidth: originalSize.width,
                    originalHeight: originalSize.height,
                    orientation,
                    takenAt: takenAt
                        ? new Date(takenAt as any).toISOString()
                        : null,
                    rotation: 0,
                    used: false,
                    progress: 0,
                };

                uploadedImages.push(newImage);

                setImages(prev =>
                    [...prev, newImage!].sort((a, b) =>
                        a.name.localeCompare(b.name, undefined, {
                            numeric: true,
                        })
                    )
                );

                // Upload small image.
                const { smallUrl } = await uploadSmallWithWorker(
                    new File([smallBlob], file.name, {
                        type: "image/jpeg",
                    }),
                    albumName,
                    id,
                    file.name
                );

                setImages(prev =>
                    prev.map(img =>
                        img.id === id
                            ? {
                                ...img,
                                smallUrl: smallUrl ?? img.smallUrl,
                            }
                            : img
                    )
                );

                URL.revokeObjectURL(tempSmallUrl);

                // Upload original and wait for server confirmation.
                const originalUrl = await enqueueOriginalUpload({
                    file,
                    albumName,
                    id,
                    token,
                    apiUrl: process.env.REACT_APP_API_URL,
                });

                newImage.originalUrl = originalUrl;

            } catch (err) {
                failedUploads.push(file.name);

                console.error(
                    "UPLOAD FAILED",
                    file.name,
                    err
                );

                // Don't leave a failed new image as an existing library entry.
                if (newImage) {
                    setImages(prev =>
                        prev.filter(img => img.id !== newImage!.id)
                    );
                }
            } finally {
                updateTaskProgress();
            }
        };

        // EXISTING IMAGE: repair its original without creating a new entry.
        const repairExistingOriginal = async (file: File) => {
            const existingImage = images.find(
                img => img.name === file.name
            );

            try {
                if (!existingImage) {
                    throw new Error(
                        `Existing image not found: ${file.name}`
                    );
                }

                const originalUrl = await enqueueOriginalUpload({
                    file,
                    albumName,
                    id: existingImage.id,
                    token,
                    apiUrl: process.env.REACT_APP_API_URL,
                });

                setImages(prev =>
                    prev.map(img =>
                        img.id === existingImage.id
                            ? {
                                ...img,
                                originalUrl,
                                progress: 100,
                            }
                            : img
                    )
                );

                repairedOriginals.push(file.name);

                console.info(
                    "Original repaired successfully:",
                    file.name
                );

            } catch (err) {
                failedUploads.push(file.name);

                console.error(
                    "ORIGINAL REPAIR FAILED",
                    file.name,
                    err
                );
            } finally {
                updateTaskProgress();
            }
        };

        try {
            // Queue new images.
            newFiles.forEach(file => {
                uploadManager.add(() => processFile(file));
            });

            // Queue original-only repairs for existing images.
            repairFiles.forEach(file => {
                uploadManager.add(() => repairExistingOriginal(file));
            });

            // Wait for all queued tasks, including original uploads.
            await uploadManager.waitForIdle();

            // Save metadata for successfully uploaded NEW images only.
            const successfullyUploadedImages = uploadedImages.filter(
                img => Boolean(img.originalUrl)
            );

            if (
                onImagesSaved &&
                successfullyUploadedImages.length > 0
            ) {
                const metadataPayload = successfullyUploadedImages.map(img => ({
                    id: img.id,
                    name: img.name,
                    orientation: img.orientation,
                    width: img.originalWidth ?? img.width,
                    height: img.originalHeight ?? img.height,
                    takenAt: img.takenAt ?? null,
                    rotation: img.rotation ?? 0,
                    used: img.used ?? false,
                }));

                await onImagesSaved(metadataPayload);
            }

            // Warn about duplicate filenames within the selected batch.
            if (duplicates.length > 0) {
                const message =
                    duplicates.length === 1
                        ? `The image "${duplicates[0]}" was selected more than once.`
                        : `These images were selected more than once:\n${duplicates.join("\n")}`;

                showUploadMessage(message, 5000);
            }

            // Final result after ALL tasks have finished.
            if (failedUploads.length > 0) {
                setImportMessage(
                    `Import incomplete: ${failedUploads.length} image(s) failed. Please retry them.`
                );
            } else if (repairedOriginals.length > 0) {
                setImportMessage(
                    `Upload finished! Repaired ${repairedOriginals.length} original image(s).`
                );
            } else {
                setImportMessage("Upload finished!");
            }

            setImportProgress(1);

            setTimeout(
                () => setShowImportOverlay(false),
                failedUploads.length > 0 ? 5000 : 1500
            );

        } catch (err) {
            console.error("Image import failed:", err);

            setImportMessage(
                "Import could not be completed. Please check the console and retry."
            );

            setImportProgress(1);

            setTimeout(
                () => setShowImportOverlay(false),
                5000
            );

        } finally {
            setIsImporting(false);
            completedRef.current = 0;
        }
    };



    // fetch saved images
    useEffect(() => {
        const fetchSavedImages = async () => {
            if (!albumName || !username || !token) return;

            try {
                // --- Fetch images ---
                const res = await fetch(
                    `${API_URL}/images/${encodeURIComponent(albumName)}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                if (!res.ok) {
                    console.warn(`Failed to fetch images for album ${albumName}`);
                    return;
                }

                const savedImages: AlbumImage[] = await res.json();


                // --- Fetch metadata ---
                let metadata: any[] = [];

                try {
                    const resMeta = await fetch(
                        `${API_URL}/albums/${encodeURIComponent(username)}/${encodeURIComponent(albumName)}/metadata`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );

                    if (resMeta.ok) {
                        metadata = await resMeta.json();
                    }

                } catch {
                    metadata = [];
                }


                // --- Map metadata by filename ---
                const metadataMap = new Map<string, any>();

                metadata.forEach(m => {
                    metadataMap.set(m.name, m);
                });

                // --- Merge saved images + metadata ---
                const imagesWithMetadata: AlbumImage[] = savedImages.map(img => {

                    const meta = metadataMap.get(img.name);

                    const orientation =
                        meta?.orientation ??
                        img.orientation ??
                        (
                            img.width && img.height
                                ? img.width >= img.height
                                    ? "horizontal"
                                    : "vertical"
                                : "horizontal"
                        );

                    return {
                        ...img,

                        // ID from metadata if available
                        id: meta?.id ?? img.id,

                        // used for stage dropping
                        orientation,

                        // dimensions
                        width: meta?.width ?? img.width,
                        height: meta?.height ?? img.height,

                        originalWidth:
                            meta?.width ??
                            img.originalWidth,

                        originalHeight:
                            meta?.height ??
                            img.originalHeight,

                        // EXIF capture date
                        takenAt:
                            meta?.takenAt ??
                            img.takenAt ??
                            null,

                        // extra data
                        rotation:
                            meta?.rotation ??
                            img.rotation ??
                            0,

                        used:
                            meta?.used ??
                            img.used ??
                            false,
                    } as AlbumImage;
                });



                // --- Add to state without duplicates ---
                setImages(prev => {

                    const existingIds = new Set(
                        prev.map(img => img.id)
                    );


                    const newImages =
                        imagesWithMetadata.filter(
                            img => !existingIds.has(img.id)
                        );


                    return [
                        ...prev,
                        ...newImages
                    ];
                });


            } catch (err) {
                console.warn(
                    "Could not fetch album images or metadata",
                    err
                );
            }

        };


        fetchSavedImages();

    }, [albumName, username, token]);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && token) {
            handleFiles(e.target.files); //  pass token here
        }
    };

    // Merge defaultImages and usedImages without duplicates
    const [images, setImages] = useState<AlbumImage[]>(() => {
        const allImages = [...defaultImages];
        const existingIds = new Set(allImages.map(img => img.id));

        usedImages.forEach(img => {
            if (!existingIds.has(img.id)) allImages.push(img);
        });

        // Mark used images
        const usedSet = new Set(
            usedImages.map(u => u.smallUrl) // or originalUrl, whichever you want to match
        );

        return allImages.map(img => ({
            ...img,
            used: usedSet.has(img.smallUrl),
        }));
    });


    // Delete image by id + filename
    const handleDeleteImage = async (id: string, filename: string) => {
        try {
            const res = await fetch(`${API_URL}/images/${username}/${albumName}/${filename}?id=${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!res.ok) {
                const text = await res.text();
                showUploadMessage(`Could not delete image: ${text}`);
                return;
            }

            // Remove from UI if delete succeeded
            setImages((imgs) => imgs.filter((img) => img.id !== id));
            setSelectedImageIds((prev) => prev.filter((x) => x !== id));

        } catch (err) {
            showUploadMessage("Could not delete image. Check console for details.");
        }
    };

    // Multi-delete selected images (only unused)
    const handleDeleteSelectedImages = useCallback(async () => {
        if (selectedImageIds.length === 0) return;

        // Only images actually selected
        const imagesToDelete = images.filter(img => selectedImageIds.includes(img.id));

        // Split into deletable (unused) and not deletable (used) images
        const deletableImages = imagesToDelete.filter(img => !img.used);
        const notDeletableImages = imagesToDelete.filter(img => img.used);

        const idsToDelete = deletableImages.map(img => img.id);
        const filenamesToDelete = deletableImages.map(img => img.name);

        try {
            if (idsToDelete.length > 0) {
                const res = await fetch(`${API_URL}/images/${username}/${albumName}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ ids: idsToDelete, filenames: filenamesToDelete }),
                });

                if (!res.ok) {
                    const text = await res.text();
                    showUploadMessage(`Failed to delete images: ${text}`);
                    return;
                }

                // Remove deleted images from UI
                setImages(prev => prev.filter(img => !idsToDelete.includes(img.id)));
            }

            // Clear selection for deleted images
            setSelectedImageIds([]);

            // Build message in English
            let message = '';
            const deletedCount = deletableImages.length;
            const notDeletedCount = notDeletableImages.length;

            if (deletedCount > 0) {
                message += `${deletedCount} image${deletedCount === 1 ? '' : 's'} deleted.`;
            }

            if (notDeletedCount > 0) {
                if (message) message += ' ';
                message += `${notDeletedCount} image${notDeletedCount === 1 ? '' : 's'} in use and not deleted.`;
            }

            showUploadMessage(message);

        } catch (err) {
            showUploadMessage('Error deleting images. Check the console.');
            console.error(err);
        }
    }, [selectedImageIds, images, username, albumName, token]);



    // Rotate the image
    const handleRotateImage = async (img: AlbumImage, direction: "left" | "right") => {
        try {
            const res = await fetch(`${API_URL}/images/rotate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    user: username,
                    album: albumName,
                    filename: img.name,
                    direction,
                }),
            });

            const data = await res.json();
            if (!data.success) throw new Error(data.error || "Rotation failed");

            //  Refresh small/original URLs silently (no animation)
            setImages((prev) =>
                prev.map((x) =>
                    x.id === img.id
                        ? {
                            ...x,
                            smallUrl: x.smallUrl.split("?")[0] + "?v=" + Date.now(),
                            originalUrl: x.originalUrl.split("?")[0] + "?v=" + Date.now(),
                        }
                        : x
                )
            );
        } catch (err: any) {
            showUploadMessage("Rotation failed " + err.message);
        }
    };


    // Notify parent when images change
    const prevImagesRef = useRef<AlbumImage[]>([]);
    useEffect(() => {
        if (images.length > prevImagesRef.current.length) {
            onImagesUploaded?.(images);
        }
        prevImagesRef.current = images;
    }, [images, onImagesUploaded]);

    useEffect(() => {
        if (!usedImages) return;

        setImages(prev =>
            prev.map(img => {
                const usedImg = usedImages.find(u => u.id === img.id);
                return usedImg ? { ...img, used: true } : { ...img, used: false };
            })
        );
    }, [usedImages]);


    ///Delete many images
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const isDeleteKey =
                e.key === "Delete" || e.key === "Backspace";

            if (isDeleteKey && e.shiftKey && selectedImageIds.length > 0) {
                e.preventDefault();
                handleDeleteSelectedImages();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedImageIds, handleDeleteSelectedImages]);



    // --- Define filter type ---
    type FilterType = "all" | "used" | "unused" | "horizontal" | "vertical";
    const [activeFilter, setActiveFilter] = useState<FilterType>("all");

    // --- Count images dynamically using orientations ---
    const counts = {
        all: images.length,
        used: images.filter(img => img.used).length,
        unused: images.filter(img => !img.used).length,
        horizontal: images.filter(img => img.orientation === "horizontal").length,
        vertical: images.filter(img => img.orientation === "vertical").length,
    };

    // --- Compute displayed images based on active filter ---
    const displayedImages = [...images]
        .filter(img => {
            switch (activeFilter) {
                case "used":
                    return img.used;
                case "unused":
                    return !img.used;
                case "horizontal":
                    return img.orientation === "horizontal";
                case "vertical":
                    return img.orientation === "vertical";
                default:
                    return true;
            }
        })

        .sort((a, b) => {
            let result = 0;

            switch (sortMode) {
                case "name":
                    result = a.name.localeCompare(b.name, undefined, {
                        numeric: true,
                        sensitivity: "base",
                    });
                    break;

                case "dateTaken": {
                    const aTime = a.takenAt
                        ? new Date(a.takenAt).getTime()
                        : Number.MAX_SAFE_INTEGER;

                    const bTime = b.takenAt
                        ? new Date(b.takenAt).getTime()
                        : Number.MAX_SAFE_INTEGER;

                    result = aTime - bTime;
                    break;
                }

                case "size": {
                    const sizeA = (a.width ?? 0) * (a.height ?? 0);
                    const sizeB = (b.width ?? 0) * (b.height ?? 0);

                    result = sizeB - sizeA;
                    break;
                }

                default:
                    result = 0;
            }

            return sortDirection === "asc" ? result : -result;

        });



    // zoom the images
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!zoomedImage) return;

            // prevent parent from using these keys
            if (["ArrowLeft", "ArrowRight", "Escape"].includes(e.key)) {
                e.stopPropagation();
                e.preventDefault();
            }

            if (e.key === "Escape") {
                setZoomedImage(null);
            } else if (e.key === "ArrowLeft") {
                setZoomedImage((prev) => {
                    if (!prev) return null;
                    const idx = displayedImages.findIndex((img) => img.id === prev.id);
                    const prevIdx = (idx - 1 + displayedImages.length) % displayedImages.length;
                    return displayedImages[prevIdx];
                });
            } else if (e.key === "ArrowRight") {
                setZoomedImage((prev) => {
                    if (!prev) return null;
                    const idx = displayedImages.findIndex((img) => img.id === prev.id);
                    const nextIdx = (idx + 1) % displayedImages.length;
                    return displayedImages[nextIdx];
                });
            }
        };

        if (zoomedImage) {
            window.addEventListener("keydown", handleKeyDown, true); // capture phase
        }
        return () => {
            window.removeEventListener("keydown", handleKeyDown, true);
        };

    }, [zoomedImage, displayedImages]);


   
    return (
        <div style={{ height: "100vh", width: width,  display: "flex", flexDirection: "column", fontFamily: "Arial, sans-serif" }}>

            {/* // Login bar*/}
            <div
                style={{
                    height:61,
                    borderRight: "1px solid #b1b1b1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 10px",
                    fontSize: 15,
                    backgroundColor: "#fff",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                }}
            >
                <img
                    src={`${process.env.PUBLIC_URL}/user_login.png`}
                    alt="Online"
                    className="status-icon"
                />
                <div>
                    <input
                        type="text"
                        value={username ?? ""}
                        readOnly
                        className="username-field2"
                    />
                </div>

                {/* Right side: export button + menu */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Tooltip title="Export Pages" arrow>
                        <span>
                            <button className="icon-btn" onClick={handleClick}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/export_pages.png`}
                                    alt="Export"
                                    style={{ width: 32, height: 32 }}
                                />
                            </button>
                        </span>
                    </Tooltip>

                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                onExportProof(); // PDF proof
                            }}
                            style={{ fontSize: "14px" }}
                        >
                            Export Proof (PDF)
                    </MenuItem>
                        <MenuItem
                            disabled={disableWallArts}
                            onClick={() => {
                                handleClose();
                                onExportWebProof();
                            }}
                            style={{ fontSize: "14px" }}
                        >
                            Export Online Proof (Web)
                    </MenuItem>
                    <MenuItem
                            onClick={printExported ? undefined : handlePrint}
                            disabled={printExported}
                            style={{
                                fontSize: "14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: printExported ? "#888" : "inherit",
                                pointerEvents: printExported ? "none" : "auto",
                                opacity: printExported ? 0.6 : 1,
                            }}
                        >
                            {printExported && <LockIcon fontSize="small" />}
                            Export for Print
                        </MenuItem>
                        <MenuItem
                            disabled={disableWallArts || !printExported}
                            onClick={() => {
                                handleClose()
                                if (!printExported) {
                                    // optionally alert user
                                    alert("You must first export the album for print!");
                                    return;
                                }
                                if (onRequestSlideshowNames) {
                                    onRequestSlideshowNames(); // open names/music dialog
                                }
                            }}
                            style={{
                                fontSize: "14px",
                                color: printExported ? "inherit" : "#888", // greyed out if print not exported
                                cursor: printExported ? "pointer" : "not-allowed",
                            }}
                        >
                            Export SlideShow (MP4)
                        </MenuItem>
                </Menu>            
                </div>
            </div>



            {/* // Image bar */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column", // 👈 makes 2 rows
                    justifyContent: "center",
                    padding: "6px 10px",
                    fontSize: 15,
                    backgroundColor: "#fff",
                    position: "sticky",
                    top: 50,
                    zIndex: 10,
                    fontWeight: "bold",
                    color: "#6f6f6f",

                    borderTop: "1px solid #b1b1b1",
                    borderRight: "1px solid #b1b1b1",
                    borderBottom: "1px solid #b1b1b1",

                    gap: 4,
                }}
            >
                {/* ===================== ROW 1 ===================== */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {uploadMessage && (
                        <div className={`upload-message ${fade ? "fade-out" : ""}`}>
                            {uploadMessage.split("\n").map((line, i) => (
                                <div key={i}>{line}</div>
                            ))}
                        </div>
                    )}

                    <div className="ImagesName-700">Images</div>

                    <Tooltip title="Upload Images" arrow>
                        <span>
                            <button
                                className="icon-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/import_images.png`}
                                    alt="Import"
                                />
                            </button>
                        </span>
                    </Tooltip>

                    <input
                        type="file"
                        accept="image/jpeg,image/jpg"
                        multiple
                        style={{ display: "none" }}
                        ref={fileInputRef}
                        onChange={onFileChange}
                        aria-hidden="true"
                    />

                    <div className="text-sm text-gray-600">
                        {displayedImages.length} images
                    </div>
                </div>

                {/* ===================== ROW 2 (NEW: SHORT / SORT) ===================== */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 15,
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#666",

                        paddingTop: 6,
                        paddingBottom: 1,

                        borderTop: "1px solid #b1b1b1",
                        marginLeft: -11,
                        marginRight: -14,
                        paddingLeft: 14,
                        paddingRight: 14,
                    }}
                >
                    <span>Sort:</span>

                    <select
                        value={sortMode}
                        onChange={(e) => setSortMode(e.target.value as SortMode)}
                        style={{
                            padding: "2px 6px",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            background: "#fff",
                            color: "#333",
                            fontSize: 13,
                            outline: "none",
                            transition: "0.15s ease",
                        }}
                        onFocus={(e) => {
                            e.currentTarget.style.borderColor = "#4a90e2";
                            e.currentTarget.style.boxShadow = "0 0 0 2px rgba(74,144,226,0.25)";
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = "#ccc";
                            e.currentTarget.style.boxShadow = "none";
                        }}
                    >
                        <option value="name">File Name</option>
                        <option value="dateTaken">Date Taken</option>
                        <option value="size">By Size</option>
                    </select>

                    {/* Sort direction */}
                    <div style={{ display: "flex", gap: 2, marginLeft: "auto" }}>
                        <button
                            type="button"
                            onClick={() => setSortDirection("asc")}
                            title="Ascending"
                            style={{
                                width: 28,
                                height: 24,
                                padding: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                backgroundColor:
                                    sortDirection === "asc" ? "#e6f0ff" : "#f7f7f7",
                                border: sortDirection === "asc" ? "1px solid #007bff" : "1px solid #bbb",
                                color: "#666",
                                cursor: "pointer",
                                borderRadius: 2,
                            }}
                        >
                            <span style={{ fontSize: 11, fontWeight: 700 }}>A</span>
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                        </button>

                        <button
                            type="button"
                            onClick={() => setSortDirection("desc")}
                            title="Descending"
                            style={{
                                width: 28,
                                height: 24,
                                padding: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                backgroundColor:
                                    sortDirection === "desc" ? "#e6f0ff" : "#f7f7f7",
                                border: sortDirection === "desc" ? "1px solid #007bff" : "1px solid #bbb",
                                color: "#666",
                                cursor: "pointer",
                                borderRadius: 3,
                            }}
                        >
                            <span style={{ fontSize: 11, fontWeight: 700 }}>D</span>
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                        </button>
                    </div>
                </div>
            </div>

            {/* thumbnails */}
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#fafafa",
                    boxSizing: "border-box",
                    overflowX: "hidden",
                    overflowY: "auto",
                    padding: 5,
                    position: "relative",
                    alignItems: "flex-start",
                    borderBottom: "1px solid #b1b1b1",
                    borderRight: "1px solid #b1b1b1",
                    paddingBottom: 30,
                    zIndex: 100,
                }}
                onClick={(e) => {
                    const clickedOnItem = (e.target as HTMLElement).closest(".thumbnail-item");

                    if (!clickedOnItem) {
                        setSelectedImageIds([]);
                        setLastSelectedIndex(null);
                    }
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 132px))`,
                        gap: 8,
                        width: "100%",
                        alignContent: "start",
                    }}
                >
                    {displayedImages.map((img, i) => {
                        const isSelected = selectedImageIds.includes(img.id);
                        const showImageNumber = img.name.length > 15;

                        const imageNumber =
                            img.name.match(/(\d+)(?=\.[^.]+$)/)?.[1] ?? img.name;

                        return (
                            <div
                                key={img.id}
                                draggable={!isImporting}
                                className="thumbnail-item"
                                onDragOver={(e) => e.preventDefault()}
                                onDragStart={(e) => {
                                    const imagesToDrag = selectedImageIds.includes(img.id)
                                        ? images.filter((x) => selectedImageIds.includes(x.id))
                                        : [img];
                         
                                    const payload =
                                        imagesToDrag.length === 1
                                            ? {
                                                id: imagesToDrag[0].id,
                                                name: imagesToDrag[0].name,
                                                originalUrl: imagesToDrag[0].originalUrl,
                                                smallUrl: imagesToDrag[0].smallUrl,
                                                orientation: imagesToDrag[0].orientation,
                                                width: imagesToDrag[0].width,
                                                height: imagesToDrag[0].height,
                                            }
                                            : imagesToDrag.map(image => ({
                                                id: image.id,
                                                name: image.name,
                                                originalUrl: image.originalUrl,
                                                smallUrl: image.smallUrl,
                                                orientation: image.orientation,
                                                width: image.width,
                                                height: image.height,
                                            }));

                                    e.dataTransfer.setData("application/json", JSON.stringify(payload));

                                    const dragImg = new Image();
                                    dragImg.src =
                                        "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
                                    e.dataTransfer.setDragImage(dragImg, 0, 0);
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const clickedIndex = i;

                                    // SHIFT-CLICK → select range
                                    if (e.shiftKey && lastSelectedIndex !== null) {
                                        const start = Math.min(lastSelectedIndex, clickedIndex);
                                        const end = Math.max(lastSelectedIndex, clickedIndex);

                                        const rangeIds = displayedImages
                                            .slice(start, end + 1)
                                            .map(img => img.id);

                                        setSelectedImageIds(rangeIds);
                                        return;
                                    }

                                    // CTRL or ⌘ → toggle selection
                                    if (e.ctrlKey || e.metaKey) {
                                        setSelectedImageIds(prev =>
                                            prev.includes(img.id)
                                                ? prev.filter(id => id !== img.id)
                                                : [...prev, img.id]
                                        );
                                        setLastSelectedIndex(clickedIndex);
                                        return;
                                    }
                                    // NORMAL CLICK → replace selection
                                    setSelectedImageIds([img.id]);
                                    setLastSelectedIndex(clickedIndex);
                                }}
                                onDoubleClick={() => setZoomedImage(img)}
                                onMouseEnter={() => setHoveredIndex(i)}
                                onMouseLeave={() => setHoveredIndex(null)}
                                style={{
                                    userSelect: "none",
                                    borderRadius: 5,
                                    backgroundColor: isSelected ? "#d0ebff" : "#ebebeb",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    padding: 0,
                                    boxSizing: "border-box",
                                    position: "relative",
                                    cursor: "pointer",
                                  //  width: 132,
                                    height: 136,
                                    boxShadow: "0 2px 3px rgba(0,0,0,0.35)",
                                    border: isSelected
                                        ? "2px solid #59b3d8"
                                        : "2px solid transparent",
                                }}
                                tabIndex={0}
                            >
                                {/* Image container */}
                                <div
                                    style={{
                                        width: 112,
                                        height: 112,
                                        borderRadius: 4,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        position: "relative",
                                    }}
                                >
                                    <img
                                        src={img.smallUrl}
                                        alt={img.name}
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: "90%",
                                            objectFit: "contain",
                                            pointerEvents: "none",
                                            userSelect: "none",
                                            opacity: img.used ? 0.4 : 1,
                                            //transform: `rotate(${img.rotation || 0}deg)`,
                                            transition: "transform 0.3s ease",
                                        }}
                                        draggable={false}
                                    />

                                    {hoveredIndex === i && showImageNumber && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                bottom: 0,
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                background: "rgba(0,0,0,0.75)",
                                                color: "#fff",
                                                fontSize: 13,
                                                fontWeight: 600,
                                                padding: "3px 8px",
                                                borderRadius: 4,
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {imageNumber}
                                        </div>
                                    )}

                                    {/* Delete button (top-right corner) */}
                                    {hoveredIndex === i && !img.used && (
                                        <Tooltip title="Delete" arrow placement="top">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteImage(img.id, img.name);
                                                    setSelectedImageIds((prev) =>
                                                        prev.filter((x) => x !== img.id)
                                                    );
                                                }}
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    right: -3,
                                                    width: 21,
                                                    height: 21,
                                                    borderRadius: "50%",
                                                    border: "1px solid #ccc",
                                                    cursor: "pointer",
                                                    fontWeight: "bold",
                                                    color: "#6f6f6f",
                                                }}
                                            >
                                                ×
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>

                                {/* filename + rotate buttons */}
                                <div
                                    style={{
                                        width: 112,
                                        height: 22,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: isSelected ? "#d0ebff" : "#ebebeb",
                                        borderTop: "1px solid #ccc",
                                        borderBottomLeftRadius: 4,
                                        borderBottomRightRadius: 4,
                                        boxSizing: "border-box",
                                        position: "relative",
                                    }}
                                >
                                    {/* Rotate Left */}
                                    {hoveredIndex === i && !img.used && (
                                        <Tooltip title="Rotate Left" arrow placement="top">
                                            <button
                                                
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await handleRotateImage(img, "left");
                                                }}
                                                style={{
                                                    position: "absolute",
                                                    left: -3,                                                 
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    border: "1px solid #ccc",
                                                    cursor: "pointer",
                                                    fontSize: 12,
                                                    fontWeight: "bold",
                                                    lineHeight: 1,
                                                    color: "#6f6f6f",
                                                }}
                                            >
                                                ↺
                                            </button>
                                        </Tooltip>
                                    )}

                                    {/* filename */}
                                    <div
                                        style={{
                                            flex: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            textAlign: "center",
                                            padding: "0 4px",
                                            color: "#6f6f6f",
                                            userSelect: "text",
                                        }}
                                        title={img.name}
                                    >
                                        {img.name}
                                    </div>

                                    {/* Rotate Right */}
                                    {hoveredIndex === i && !img.used && (
                                        <Tooltip title="Rotate Right" arrow placement="top">
                                            <button
                                               
                                                onClick={async (e) => {
                                                    e.stopPropagation();                                            
                                                    await handleRotateImage(img, "right");
                                                }}
                                                style={{
                                                    position: "absolute",
                                                    right: -3,
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    border: "1px solid #ccc",
                                                    cursor: "pointer",
                                                    fontSize: 12,
                                                    fontWeight: "bold",
                                                    lineHeight: 1,
                                                    color: "#6f6f6f",
                                                }}
                                            >
                                                ↻
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {/* Overlay while importing */}
                    {isImporting && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(255, 255, 255, 0.5)",
                                zIndex: 999,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#333",
                            }}
                        >
                                          
                        </div>
                    )}
                </div>
                {showImportOverlay && (
                    <div
                        style={{
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: "#000",
                            color: "#fff",
                            padding: "20px 30px",
                            borderRadius: 8,
                            fontSize: 16,
                            fontWeight: 700,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            zIndex: 2000,
                            minWidth: 200,
                            textAlign: "center",
                        }}
                    >
                        <div>{importMessage}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                            {Array.from({ length: 6 }).map((_, i) => {
                                const fillIndex = Math.floor(importProgress * 6);
                                return (
                                    <div
                                        key={i}
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: 2,
                                            backgroundColor: i < fillIndex ? "#fff" : "#555",
                                            transition: "background-color 0.2s",
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                {zoomedImage && (
                    <div
                        onClick={() => setZoomedImage(null)}
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100vw",
                            height: "100vh",
                            backgroundColor: "rgba(0,0,0,0.7)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 9999,
                            overflow: "hidden", // prevent scrollbars
                        }}
                        aria-modal="true"
                        role="dialog"
                        tabIndex={-1}
                    >
                        {/* LEFT ARROW */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const currentIndex = displayedImages.findIndex((x) => x.id === zoomedImage.id);
                                if (currentIndex > 0) {
                                    setZoomedImage(displayedImages[currentIndex - 1]);
                                }
                            }}
                            style={{
                                position: "absolute",
                                left: 30,
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: 40,
                                color: "white",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                        >
                            ‹
                        </button>

                     
                        {/* LARGE IMAGE + FILENAME */}
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}
                        >
                            <img
                                src={zoomedImage.smallUrl}
                                alt={zoomedImage.name}
                                onLoad={(e) => {
                                    const img = e.currentTarget;
                                    setZoomedImageOrientation(
                                        img.naturalWidth >= img.naturalHeight
                                            ? "horizontal"
                                            : "vertical"
                                    );
                                }}
                                style={{
                                    width: "auto",
                                    height: "auto",
                                    objectFit: "contain",
                                    borderRadius: 8,
                                    maxWidth: "90vw",
                                    maxHeight: "85vh",
                                    boxShadow: "0 0 10px rgba(0,0,0,0.5)",
                                    cursor: "zoom-out",
                                }}
                                draggable={false}
                            />

                            {/* filename */}
                            <div
                                style={{
                                    marginTop: 12,
                                    color: "white",
                                    fontSize: 16,
                                    fontWeight: 500,
                                    background: "rgba(0,0,0,0.55)",
                                    padding: "6px 14px",
                                    borderRadius: 6,
                                    maxWidth: "80vw",
                                    textAlign: "center",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={zoomedImage.name}
                            >
                                {zoomedImage.name}
                            </div>
                        </div>

                        {/* RIGHT ARROW */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const currentIndex = displayedImages.findIndex((x) => x.id === zoomedImage.id);
                                if (currentIndex < displayedImages.length - 1) {
                                    setZoomedImage(displayedImages[currentIndex + 1]);
                                }
                            }}
                            style={{
                                position: "absolute",
                                right: 30,
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: 40,
                                color: "white",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                        >
                            ›
                        </button>
                    </div>
                )}

            </div>



            {/* bottom bar */}
            <div
                style={{
                    height: 34,
                    borderLeft: "1px solid #ddd",
                    borderRight: "1px solid #b1b1b1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    padding: "0 8px",
                    fontSize: 14,
                    backgroundColor: "#fff",
                    position: "sticky",  // keep it sticky
                    bottom: 0,           // stick to bottom
                    zIndex: 10,
                    gap: 12, // spacing between groups
                    flexShrink: 0,       // prevent collapsing
                }}
            >
               
                    <>
                        {/* --- Reset button first --- */}
                        <Tooltip title="Filters Off" arrow>
                            <button
                                onClick={() => setActiveFilter("all")}
                                style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: 4,
                                    border: activeFilter === "all" ? "1px solid #007bff" : "1px solid #bbb",
                                    backgroundColor: activeFilter === "all" ? "#e6f0ff" : "#f7f7f7",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 0,
                                }}
                            >
                                    <img 
                                       src={`${process.env.PUBLIC_URL}/remove_filters.png`} 
                                        alt="reset" 
                                         style={{ width: 14, height: 14 }} 
                                       />
                            </button>
                        </Tooltip>

                            {/* --- Usage filters group --- */}
                            <div style={{ display: "flex", gap: 6 }}>
                                {[
                                    { id: "used", icon: `${process.env.PUBLIC_URL}/used_images_icon.png`, title: `Used (${counts.used})` },
                                    { id: "unused", icon: `${process.env.PUBLIC_URL}/unused_images_icon.png`, title: `Unused (${counts.unused})` },
                                ].map(btn => (
                                    <Tooltip key={btn.id} title={btn.title} arrow>
                                        <button
                                            onClick={() => setActiveFilter(btn.id as FilterType)}
                                            style={{
                                                width: 22,
                                                height: 22,
                                                borderRadius: 2,
                                                border: activeFilter === btn.id ? "1px solid #007bff" : "1px solid #bbb",
                                                backgroundColor: activeFilter === btn.id ? "#e6f0ff" : "#f7f7f7",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: 0,
                                                position: "relative",
                                            }}
                                        >
                                            <img
                                                src={btn.icon}
                                                alt={btn.id}
                                                style={{
                                                    width: 12,
                                                    height: 12,
                                                    opacity: activeFilter === btn.id ? 1 : 0.7,
                                                    filter: activeFilter === btn.id ? "none" : "grayscale(50%)",
                                                }}
                                            />
                                        </button>
                                    </Tooltip>
                                ))}                        
                        {/* --- Orientation filters group --- */}               
                            {[
                                    { id: "horizontal", icon: `${process.env.PUBLIC_URL}/horizontal_image_icon.png`, title: `Horizontal (${counts.horizontal})` },
                                     { id: "vertical", icon: `${process.env.PUBLIC_URL}/vertical_image_icon.png`, title: `Vertical (${counts.vertical})` },
                            ].map(btn => (
                                <Tooltip key={btn.id} title={btn.title} arrow>
                                    <button
                                        onClick={() => setActiveFilter(btn.id as FilterType)}
                                        style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: 2,
                                            border: activeFilter === btn.id ? "1px solid #007bff" : "1px solid #bbb",
                                            backgroundColor: activeFilter === btn.id ? "#e6f0ff" : "#f7f7f7",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 0,
                                        }}
                                    >
                                        <img
                                            src={btn.icon}
                                            alt={btn.id}
                                            style={{
                                                width: 12,
                                                height: 12,
                                                opacity: activeFilter === btn.id ? 1 : 0.7,
                                                filter: activeFilter === btn.id ? "none" : "grayscale(50%)",
                                            }}
                                        />
                                    </button>
                                </Tooltip>
                            ))}
                          </div>
                            {/* Columns selector */}
                            <div
                                style={{
                                    padding: "5px 10px",
                                    borderTop: "1px solid #ccc",
                                    borderRadius: 4,
                                    textAlign: "center",
                                    marginLeft: "auto",
                                    background: "#fff",
                                }}
                            >                           
                        <select
                            value={columns ?? 2}
                            onChange={(e) => {
                                const newCols = Number(e.target.value);

                                onThumbnailColumnsChange?.(newCols);

                                const newWidth =
                                    newCols * 132 + (newCols - 1) * 8 + 10;

                                setWidth?.(newWidth);
                            }}
                            style={{
                                padding: "2px 6px",
                                border: "1px solid #ccc",
                                borderRadius: 4,
                                background: "#fff",
                                color: "#333",
                                fontSize: 13,
                                outline: "none",
                                transition: "0.15s ease",
                            }}
                            onFocus={(e) => {
                                e.currentTarget.style.borderColor = "#4a90e2";
                                e.currentTarget.style.boxShadow = "0 0 0 2px rgba(74,144,226,0.25)";
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = "#ccc";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                        >
                                    {[2, 3, 4].map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                    </>
              

            </div>



        </div>
    );
};

export default PhotoUploader;